// Copyright (C) 2026 Skylark Drones

const FORMULA_PREFIX = /^\s*[=+\-@]/
const UNSAFE_CONTROL_CHARACTERS =
  /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/

export function sanitizeCSVCell(value) {
  const cell = String(value === undefined || value === null ? '' : value)

  return FORMULA_PREFIX.test(cell) ? `'${cell}` : cell
}

export function validateCSVRows(rows) {
  for (const [rowIndex, row] of rows.entries()) {
    for (const [columnIndex, value] of row.entries()) {
      if (UNSAFE_CONTROL_CHARACTERS.test(String(value || ''))) {
        return {
          valid: false,
          message: `The CSV contains unsafe control characters at row ${
            rowIndex + 1
          }, column ${columnIndex + 1}.`,
          rowIndex,
          columnIndex
        }
      }
    }
  }

  return { valid: true }
}

export function parseCSVText(csvText) {
  const text = String(csvText || '').replace(/^\uFEFF/, '')
  const rows = []
  let row = []
  let cell = ''
  let inQuotes = false

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index]

    if (inQuotes) {
      if (character === '"') {
        if (text[index + 1] === '"') {
          cell += '"'
          index += 1
        } else {
          inQuotes = false
        }
      } else {
        cell += character === '\r' ? '\n' : character
        if (character === '\r' && text[index + 1] === '\n') index += 1
      }
      continue
    }

    if (character === ',') {
      row.push(cell)
      cell = ''
    } else if (character === '\r' || character === '\n') {
      row.push(cell)
      rows.push(row)
      row = []
      cell = ''
      if (character === '\r' && text[index + 1] === '\n') index += 1
    } else if (character === '"' && cell === '') {
      inQuotes = true
    } else {
      cell += character
    }
  }

  if (inQuotes) {
    return {
      valid: false,
      message: 'The CSV contains a quoted field that is not closed.'
    }
  }

  if (cell || row.length > 0) {
    row.push(cell)
    rows.push(row)
  }

  return { valid: true, rows }
}

export function sanitizeCSVRows(rows) {
  return rows.map(row => row.map(sanitizeCSVCell))
}

function escapeCSVCell(value) {
  const cell = String(value === undefined || value === null ? '' : value)
  return /[",\r\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell
}

export function serializeCSVRows(rows) {
  return rows.map(row => row.map(escapeCSVCell).join(',')).join('\r\n')
}

export function sanitizeCSVContent(csvText) {
  const parsedCSV = parseCSVText(csvText)
  if (!parsedCSV.valid) throw new Error(parsedCSV.message)

  const validation = validateCSVRows(parsedCSV.rows)
  if (!validation.valid) throw new Error(validation.message)

  return serializeCSVRows(sanitizeCSVRows(parsedCSV.rows))
}

export function downloadSanitizedCSV(data, fileName) {
  const csvContent = sanitizeCSVContent(data)
  const url = window.URL.createObjectURL(
    new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  )
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
  return true
}
